import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { useContext, useEffect, useMemo, useRef } from 'react'
import { AnalyticsContext } from '../../../context/analyticsContext'
import { CartContext } from '../../../context/cartContext'
import { LandingPageLinksContext } from '../../../context/LandingPageLinksContext'
import { PageContext } from '../../../context/PageContextProvider'
import { ViddyLPContext } from '../../../context/viddyLPContext'
import { useDynamicCartUpsells } from '../../../hooks/useDynamicCartUpsells'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import { PrimaryCTA, ProductCard } from '../../shared/ProductCard'
import type { ActiveLPLinkOptions } from '../../../context/PageContextProvider'
import type { ProductData } from '../products/ProductContent'
import React from 'react'
import {
    PRIMARY_PAGE_FILL_COLOR_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
    SUBTLE_BORDER_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { needsVariantSelectionModal, VariantSelectDialog } from './AddToCart'
import { buildCartVariant } from '../../../utils/cartTools'
import { DialogHandle } from '../../shared/Dialog'
import { scaledFontSize } from '../../../style/scrollingLayout'

export const CartUpsells: React.FC = () => {
    const { landingPage } = useContext(ViddyLPContext)
    const { setActiveLPLink, setIsGridExpanded } = useContext(PageContext)
    const { cart } = useContext(CartContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { getLinkForProduct, fetchAndCacheLinkForProduct } = useContext(
        LandingPageLinksContext,
    )
    const dynamicUpsellLinks = useDynamicCartUpsells()

    // Track product IDs we've already attempted to fetch this session so we
    // don't re-trigger fetches after dynamicLinksMap updates refresh
    // getLinkForProduct's identity.
    const fetchedProductIdsRef = useRef(new Set<string>())

    // When a cart item's product isn't in the link cache (e.g. a recovered cart
    // from a different storefront visit), fetch and cache the full link.
    useEffect(() => {
        for (const item of cart.items) {
            const productId = item.variant.link.shopifyId
            if (!productId || fetchedProductIdsRef.current.has(productId)) {
                continue
            }
            if (getLinkForProduct(productId)) continue
            fetchedProductIdsRef.current.add(productId)
            fetchAndCacheLinkForProduct(productId)
        }
    }, [cart.items, getLinkForProduct, fetchAndCacheLinkForProduct])

    const _onUpsellClick = (
        lpLink: ApiShopperLandingPageLink,
        ctx: ActiveLPLinkOptions,
    ) => {
        setActiveLPLink(lpLink, ctx)
        setIsGridExpanded(false)
        Analytics.TRACK_LANDING_PAGE_CTA_CLICK({
            type: 'cart_upsell',
        })
    }

    const staticUpsellLinks = useMemo(() => {
        return landingPage.cartUpsells.filter((link) => {
            return (
                !cart.items.some(
                    (item) => item.variant.link.linkHash === link.linkHash,
                ) &&
                link.variantInfo?.some((variant) => variant.available !== false)
            )
        })
    }, [cart.items, landingPage.cartUpsells])

    // The dynamic cart upsells setting takes precedence: when enabled, dynamic co-purchase
    // upsells (resolved from the cart's contents) replace any configured page-level list,
    // since AI create seeds every storefront with one (sc-34626).
    const upsellLinks = landingPage.dynamicCartUpsellsEnabled
        ? dynamicUpsellLinks
        : staticUpsellLinks

    // Always return a container with margin, even if there are no upsells
    return (
        <div
            style={{
                width: '100%',
                boxSizing: 'border-box',
                marginBottom: '200px',
                overflow: 'hidden',
            }}
        >
            {upsellLinks.length > 0 && (
                <>
                    <div style={{ paddingLeft: 20, paddingRight: 20 }}>
                        <h2
                            style={{
                                ...getTypographyRoleStyle('featureTitle', {
                                    fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
                                    fontWeight: 400,
                                }),
                                fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
                                marginBottom: 10,
                                paddingTop: 10,
                                color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
                            }}
                        >
                            You&apos;ll also love
                        </h2>
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem',
                            width: '100%',
                            paddingLeft: '20px',
                            paddingRight: '20px',
                            boxSizing: 'border-box',
                        }}
                    >
                        {upsellLinks.map((landingPageLink) => (
                            <div key={landingPageLink.linkHash}>
                                <CartUpsellCard
                                    landingPageLink={landingPageLink}
                                    onCardClick={_onUpsellClick}
                                />
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

const CartUpsellCard: React.FC<{
    landingPageLink: ApiShopperLandingPageLink
    onCardClick: (
        lpLink: ApiShopperLandingPageLink,
        ctx: ActiveLPLinkOptions,
    ) => void
}> = React.memo(({ landingPageLink, onCardClick }) => {
    const requiresVariantModal = needsVariantSelectionModal(landingPageLink)

    return (
        <div style={{ width: '100%', display: 'flex' }}>
            <ProductCard
                background={`var(${PRIMARY_PAGE_FILL_COLOR_VAR})`}
                border={`0.5px solid var(${SUBTLE_BORDER_COLOR_VAR})`}
                buttonBorderWidth="0.5px"
                preferAvailable={true}
                lpLink={landingPageLink}
                showRatingCount={false}
                showLimitedTimeOffer={false}
                onClick={(_e, ctx) => onCardClick(landingPageLink, ctx)}
                priceMarginTop={1}
                showSuperscriptPriceFormat={false}
                renderCTA={({ rowProductData, ctaDisabled }) => (
                    <CartUpsellCTA
                        landingPageLink={landingPageLink}
                        rowProductData={rowProductData}
                        ctaDisabled={ctaDisabled}
                        requiresVariantModal={requiresVariantModal}
                    />
                )}
            />
        </div>
    )
})
CartUpsellCard.displayName = 'CartUpsellCard'

/**
 * Cart upsell CTA. Reuses the grid's intermediate variant-select modal when
 * options need picking, otherwise adds directly using the row's active variant.
 * Respects the row's `selectedSellingPlanId` so subscription-default brands and
 * subscription-only variants add correctly; skips analytics events to preserve
 * upsells' existing event set.
 */
const CartUpsellCTA: React.FC<{
    landingPageLink: ApiShopperLandingPageLink
    rowProductData: ProductData
    ctaDisabled: boolean
    requiresVariantModal: boolean
}> = ({
    landingPageLink,
    rowProductData,
    ctaDisabled,
    requiresVariantModal,
}) => {
    const { addCartItem } = useContext(CartContext)
    const { landingPage } = useContext(ViddyLPContext)
    const dialogRef = useRef<DialogHandle>(null)

    const handleAddToCart = () => {
        addCartItem(
            buildCartVariant(
                rowProductData.activeVariant,
                landingPageLink,
                landingPage.discount ?? null,
            ),
            1,
            rowProductData.selectedSellingPlanId,
        )
    }

    return (
        <>
            <PrimaryCTA
                borderWidth="0.5px"
                text="Add to cart"
                onClick={
                    requiresVariantModal
                        ? () => dialogRef.current?.open()
                        : handleAddToCart
                }
                disabled={ctaDisabled}
            />
            {requiresVariantModal && (
                <VariantSelectDialog
                    link={landingPageLink}
                    productData={rowProductData}
                    onAddToCart={handleAddToCart}
                    innerRef={dialogRef}
                />
            )}
        </>
    )
}
